import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';
import {observable, action} from 'mobx';
import {inject, observer} from 'mobx-react';
import {routerShape, withRouter} from 'react-router';

import {browserOnly} from 'spa/Components/EnvSplit';
import {cn} from 'spa/css';
import {withI18n} from 'spa/Components/Context';

import Button from '@urbanmassage/component-library/lib/Button/Button';
import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';

import AddressField2021 from 'spa/Components/AddressField2021';
import AddressNotServiced from './AddressNotServiced';

import searchIcon from 'spa/images/icons/search_24.svg';

import './address-selector.less';

@browserOnly
@withRouter
@inject(({
  store: {
    address,
    tracker,
  },
}) => ({
  address,
  tracker,
}))
@observer
@withI18n
class AddressSelector extends React.Component {
  static propTypes = {
    address: PropTypes.shape({
      reset: PropTypes.func,
      params: PropTypes.array,
      isLoading: PropTypes.bool.isRequired,
      load: PropTypes.func.isRequired,
      search: PropTypes.object,
      response: PropTypes.object,
      error: PropTypes.object,
    }).isRequired,
    className: PropTypes.string,
    customButtonText: PropTypes.string,
    customTitle: PropTypes.string,
    isBookingLink: PropTypes.bool,
    isMobile: PropTypes.bool,
    onClearAddress: PropTypes.func.isRequired,
    onClickUseAddress: PropTypes.func.isRequired,
    router: routerShape.isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
    visibleButton: PropTypes.bool,
  };

  @observable selectedAddress;

  @autobind
  @action('onSelectAddress - AddressSelector')
  onSelectAddress(value) {
    this.selectedAddress = value;
  }

  @autobind
  @action('onClickUseAddress - AddressSelector')
  onClickUseAddress(event) {
    event.preventDefault();

    const {onClickUseAddress, tracker, trackingCategory} = this.props;

    tracker.track('Clicked Use Address', {
      category: trackingCategory,
    });

    onClickUseAddress(this.selectedAddress);
  }

  @autobind
  @action('onClickClearAddress - AddressSelector')
  onClickClearAddress(event) {
    if (event) event.preventDefault();

    const {onClearAddress, tracker, trackingCategory} = this.props;

    this.selectedAddress = null;

    if (onClearAddress) {
      onClearAddress();
    }

    tracker.track('Clicked Clear Location', {
      category: trackingCategory,
    });
  }

  render() {
    const {i18n} = this.context;
    const {selectedAddress} = this;
    const {
      address: {
        isLoading,
        response,
        error,
        search,
      },
      customButtonText,
      customTitle,
      isMobile,
      trackingCategory,
      visibleButton,
    } = this.props;

    return (
      <div>
        {isLoading ? (
          <div className="placeholder-box v-container" />
        ) : null}
        <div className={this.props.className}>
          <div className="address-selector__container">
            <div className="address-selector__sub-container">
              <div
                className={cn(
                  'address-selector__box',
                  isMobile && 'address-selector__box--is-mobile',
                )}>
                <Typography className="address-selector__title" variant={TypeVariants.h2} theme="darkTitle" margin="0 auto 2.4rem">
                  {!customTitle ? (
                    i18n._x('Where would you like your appointment?', 'Address selector')
                  ) : (
                    customTitle
                  )}
                </Typography>
                <Typography className="address-selector__copy" variant={TypeVariants.bodyXLarge} theme="secondary" margin="0 auto 2rem">{i18n._x('Enter your address to get started', 'Address picker copy')}</Typography>
                <div>
                  <div className={cn(
                    'address-selector__input',
                    error && error.json && error.json.code === 'ADDRESS_NOT_SERVICED' && 'address-selector__input--error',
                  )}>
                    <AddressField2021
                      align="left"
                      canLoadCustom
                      roundBorder
                      selectZIndex={31}
                      style={{
                        display: 'table-cell',
                        margin: 0,
                        padding: '2.5rem 5.5rem 2rem 2rem',
                        border: '0 none',
                        lineHeight: '2rem',
                        height: '6rem',
                        color: '#49485C',
                        borderRadius: '2px',
                        backgroundImage: `url(${searchIcon})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right center',
                        backgroundColor: '#FFFFFF',
                      }}
                      onSelect={this.onSelectAddress}
                      onClear={this.onClickClearAddress}
                      disabled={isLoading}
                      defaultValue={search && search.term}
                      trackingCategory={trackingCategory}
                      includeClear
                      clearStyles={{
                        position: 'absolute',
                        top: '50%',
                        translate: '0 -50%',
                        right: '3.6rem',
                        height: '2.4rem',
                        width: '2.4rem',
                      }}
                      wrap={field => (
                        <div
                          style={{
                            display: 'table',
                            tableLayout: 'fixed',
                            width: '100%',
                            position: 'relative',
                          }}>
                          {field}
                        </div>
                      )}
                    />
                  </div>
                  {visibleButton && (
                    <Button
                      block
                      data-testid="address-selector-submit"
                      onClick={this.onClickUseAddress}
                      disabled={!selectedAddress}
                    >
                      {customButtonText || i18n._x('Use this address', 'Address picker button label')}
                    </Button>
                  )}

                  {!visibleButton && selectedAddress ? (
                    <Button
                      block
                      data-testid="address-selector-submit"
                      onClick={this.onClickUseAddress}
                    >
                      {customButtonText || i18n._x('Use this address', 'Address picker button label')}
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default AddressSelector;



// WEBPACK FOOTER //
// ./spa/Components/AddressSelector/AddressSelector.jsx